import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

import { TableChartConfigDto, PitcherSessionStatsByPitchTypeChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import useReportStore from 'hooks/SessionReport/PitcherSingle/reportStore';
import PitchTypeCell from 'components/TableCells/PitchTypeCell';

export type PitcherSessionStatsByPitchTypeChartColumn = keyof PitcherSessionStatsByPitchTypeChartDataDto['statsByPitchType'][number];

const PitcherSessionStatsByPitchTypeChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<PitcherSessionStatsByPitchTypeChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PitcherSessionStatsByPitchTypeChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.statsByPitchType.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.statsByPitchType]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<PitcherSessionStatsByPitchTypeChartColumn>(extraChartData.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell: column === 'pitchType' ? (cellValue) => <PitchTypeCell pitchType={cellValue} /> : undefined,
    }));
  }, [extraChartData.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default PitcherSessionStatsByPitchTypeChart;
